import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import {
  isPushSupported,
  getPermission,
  getCurrentSubscription,
  subscribeToPush,
} from '../utils/pushNotifications';

const STORAGE_KEY = 'push-prompt-dismissed';

/**
 * Inline banner asking logged-in user to enable push notifications.
 * Auto-hides once user:
 *  - accepts (subscribes), or
 *  - dismisses (hidden for 3 days), or
 *  - already subscribed, or
 *  - permission was denied.
 */
const PushNotificationPrompt = ({ user, location }) => {
  const [visible, setVisible] = useState(false);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    if (!user || !location) return;
    if (!isPushSupported()) return;

    let cancelled = false;
    const check = async () => {
      // Permission denied? Don't nag.
      if (getPermission() === 'denied') return;

      // Already subscribed on this device?
      const existing = await getCurrentSubscription();
      if (existing) return;

      // Recently dismissed?
      const dismissed = localStorage.getItem(STORAGE_KEY);
      if (dismissed) {
        const dismissedAt = new Date(dismissed);
        const days = (Date.now() - dismissedAt.getTime()) / (1000 * 60 * 60 * 24);
        if (days < 3) return;
      }

      if (!cancelled) {
        // Delay showing by 2s so it doesn't clash with app load
        setTimeout(() => setVisible(true), 2000);
      }
    };
    check();
    return () => { cancelled = true; };
  }, [user, location]);

  const handleEnable = async () => {
    setWorking(true);
    try {
      await subscribeToPush(user, location);
      toast.success('🔔 Powiadomienia włączone!');
      setVisible(false);
    } catch (e) {
      toast.error(e.message || 'Nie udało się włączyć powiadomień');
    } finally {
      setWorking(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-prompt-banner"
      style={{
        position: 'fixed',
        bottom: '80px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        maxWidth: '480px',
        width: '90%',
        background: 'linear-gradient(135deg, #d4af37 0%, #c9a230 100%)',
        color: '#1a1a1a',
        padding: '16px 20px',
        borderRadius: '14px',
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.4)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        animation: 'slideUpPush 0.4s ease-out',
      }}
    >
      <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
        <div style={{ fontSize: '28px', lineHeight: 1 }}>🔔</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '800' }}>
            Włącz powiadomienia
          </h4>
          <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.4, opacity: 0.85 }}>
            Otrzymuj info o nowej odprawie, zgłoszonym problemie i ważnych wiadomościach managera.
          </p>
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button
              data-testid="push-prompt-enable"
              onClick={handleEnable}
              disabled={working}
              style={{
                flex: 1,
                padding: '9px 14px',
                background: '#1a1a1a',
                color: '#d4af37',
                border: 'none',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: working ? 'wait' : 'pointer',
              }}
            >
              {working ? 'Włączam…' : '✅ Włącz'}
            </button>
            <button
              data-testid="push-prompt-dismiss"
              onClick={handleDismiss}
              style={{
                padding: '9px 14px',
                background: 'transparent',
                color: '#1a1a1a',
                border: '1px solid rgba(0, 0, 0, 0.25)',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
              }}
            >
              Później
            </button>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideUpPush {
          from { transform: translate(-50%, 40px); opacity: 0; }
          to   { transform: translate(-50%, 0);    opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default PushNotificationPrompt;
